import styled from 'foundations-theming/utils';
import { getOverlayTransition } from '../Modal/ModalTransition';
import { PlainWrapper, TwoWayTransition } from '../TwoWayTransition/TwoWayTransition';
import { StyledDialog } from '../Dialog/Dialog';
import { StyledOverlayElement } from '../Overlay/Overlay';
import { ModalTransitionContextProvider } from '../Modal/ModalTransitionContext';
import { MODAL_BACKGROUND_TRANSITION_TIMING, SLIDEIN_MODAL_TRANSITION_TIMING } from '../../../constants/times';
import { jsx as _jsx } from "react/jsx-runtime";
const totalTransitionTime = parseInt(SLIDEIN_MODAL_TRANSITION_TIMING, 10);
const overlayTransitionTime = parseInt(MODAL_BACKGROUND_TRANSITION_TIMING, 10);
const dialogTransitionTime = totalTransitionTime - overlayTransitionTime;
const overlayTransition = getOverlayTransition(dialogTransitionTime);
/**
 * True whenever a transform is applied to the dialog, i.e. any time it is not
 * sitting at rest in the open position. Both the transform itself and the
 * overflow suppression that keeps it from being scrolled into view are keyed
 * off this, and they have to agree.
 */
const isTransformed = ({
  open,
  transitioning
}) => !open || transitioning;
const isExiting = ({
  open,
  transitioning
}) => transitioning && !open;

/**
 * The overlay's scroll container only needs dropping while the dialog is on its
 * way *in*: the scroll it guards against is triggered by a consumer's autoFocus,
 * which fires once as the panel mounts, and nothing inside the panel takes focus
 * on the way out.
 *
 * Excluding the exit matters for preserveScrollOnClose. Dropping the scroll
 * container clamps scrollTop to 0, and PortalLayer restores the close-transition
 * clone's scroll position during exit when that prop is set -- clipping here
 * would clamp it straight back to 0 and silently defeat it. (Without the prop
 * the clone starts at 0 regardless, so there is nothing to protect.) Only
 * reachable when the dialog is taller than the overlay at all, e.g. a
 * size="tall" min-height against a short window.
 */
const suppressesOverlayScroll = props => isTransformed(props) && !isExiting(props);
const ModalPanelTransitionWrapper = styled(PlainWrapper).attrs(({
  open,
  transitioning
}) => ({
  // Mirrors PanelTransition: makes the transition phase inspectable in the DOM
  // instead of only inferrable from computed styles, so it can be asserted in
  // tests and read directly when debugging.
  'data-component-name': 'ModalPanel',
  'data-open-complete': open && !transitioning
})).withConfig({
  displayName: "ModalPanelTransition__ModalPanelTransitionWrapper"
})(["", "{transition-property:", ";transition-delay:", "ms;transition-duration:", "ms;transform:", ";opacity:", ";}> ", "{overflow:", ";}", ";"], StyledDialog, ({
  transitionReady
}) => transitionReady ? 'transform' : 'none', overlayTransitionTime, dialogTransitionTime, props => isTransformed(props) && `translateX(${props.open ? '0' : '100'}%)`, ({
  transitioning,
  open
}) => !transitioning && !open ? '0' : '1', StyledOverlayElement, props => suppressesOverlayScroll(props) ? 'clip' : undefined, overlayTransition);
export const ModalPanelTransition = props => /*#__PURE__*/_jsx(TwoWayTransition, Object.assign({}, props, {
  ContextProvider: ModalTransitionContextProvider,
  duration: totalTransitionTime,
  transitionOnMount: true,
  Wrapper: ModalPanelTransitionWrapper
}));